"use client";
import React, { useState, useRef, useEffect, useMemo } from "react";
import styles from "./ChatInput.module.scss";
import { webSocketService } from "@/app/services/WebSocketService";
import Image from "next/image";
import EmojiPanel from "./components/EmojiPanel";
import MobileChatInput from "./components/MobileChatInput";
import { useMessageStore } from "@/app/store";
import {
  CurrentChatStatus,
  HeaderType,
  ImServiceChannel,
  MessageNotRender,
  MessageSender,
  MessageType,
  ScrollToBottom,
  SocketConnectionStatus,
} from "@/app/store/types";
import {
  ReportBizTypeEnum,
  ReportKeywordContentEnum,
  ReportParentContentEnum,
} from "@/app/types/typeConfig/report";
import { ScrollAction, ScrollFrom } from "@/app/hooks/useReadScrollToBottom";
import UploadFile from "./components/UploadFile";
import { useFilePaste } from "@/app/hooks/useFilePaste";
import PasteModal from "./PasteModal";
import { getPublicImagePath } from "@/app/utils/image";
import ChatInputQuoted from "./ChatInputQuoted";
import { useInputTyping } from "@/app/hooks/useInputTyping";
import { STOP_TYPING_DELAY } from "@/app/constants";
import { isOverseaEnv } from "@/app/utils/env";
import AutoComplete from "@/app/components/AutoComplete";
import {
  RobotChatPropsType,
  ReportEventEnum,
  ReportSceneTypeEnum,
} from "@/app/types/typeConfig/report";
import useReportRequestQueue from "@/app/hooks/robot/useReportRequestQueue";
import { createUuid } from "@/app/utils/string";
import { handleRobotChat } from "@/app/utils/bizRequest";
import { useTranslations, useCurrentLocale } from "@/app/lib/LocaleProvider";
import { useSSESend } from "@/app/hooks/robot/useSSESend";
import { useCurrentUser } from "@/app/lib/AuthProvider";
import { Button, Modal, Toast } from "@douyinfe/semi-ui";
import SvgEndChat from "@/app/icons/SvgEndChat.svg";
import SemiButton from "@/app/ui/button";
import { closeConversation, reportPush } from "@/app/api/message";
import Locale from "@/app/locales";
import { ReportScene, reportScene } from "@/app/services/DataReporter";

interface ChatInputProps {
  handleScrollToBottom: ScrollToBottom;
  pageVisible?: boolean;
}

export default function ChatInput(props: ChatInputProps) {
  const [inputVal, setInputVal] = useState<string>("");
  const [showEmojiPanel, setShowEmojiPanel] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const {
    chatStatus,
    isConnected,
    setCurrentChatStatus,
    quotedMsg,
    aiTypingEnd,
    conversationId,
    setConnectionStatus,
    reEditContent,
    setReEditContent,
    imServiceChannel,
    setImServiceChannel,
  } = useMessageStore();
  const { handlePaste, pasteFileVisible, modalHandler } = useFilePaste();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const { onchangeInput } = useInputTyping();
  const [endChatVisible, setEndChatVisible] = useState(false);
  const isComposing = useRef(false);
  const user = useCurrentUser();
  const regex = /(<([^>]+)>)/gi;
  const { addToReportQueue, requireReportProps } = useReportRequestQueue();
  const t = useTranslations();
  const locale = useCurrentLocale();

  // 使用 useSSESend Hook
  const { handleSSESend, isSending, stopStreaming } = useSSESend({
    handleScrollToBottom: props.handleScrollToBottom,
    onSendSuccess: () => {
      // setInputVal(""); // 发送成功后清空输入
    },
  });

  // 检测是否为移动端
  useEffect(() => {
    const checkIsMobile = () => {
      if (!isMobile) {
        setIsMobile(window.innerWidth <= 768);
      }
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    if (quotedMsg) {
      inputRef.current?.focus();
    }
  }, [quotedMsg]);

  // 使用 useMemo 让 disconnected 随 isConnected 变化而变化
  const disconnected = useMemo(
    () =>
      isConnected === SocketConnectionStatus.DISCONNECTED ||
      isConnected === SocketConnectionStatus.CLOSE ||
      isConnected === SocketConnectionStatus.ERROR,
    [isConnected],
  );

  // 是否显示结束会话按钮：当用户在线，并且已经进入会话（收到人工欢迎语）时
  const showEndChatButton = useMemo(
    () =>
      isConnected === SocketConnectionStatus.CONNECTED &&
      chatStatus === CurrentChatStatus.Online,
    [isConnected, chatStatus],
  );

  // 结束会话处理
  const handleEndChatOk = async () => {
    if (conversationId) {
      setConnectionStatus(SocketConnectionStatus.DISCONNECTED);
      reportScene(ReportScene.EXIT_CHAT);
      const [_, pushSatisfy] = await Promise.all([
        closeConversation(),
        reportPush(conversationId),
      ]);
      // 如果没有推送过，就再推送一条
      webSocketService.sendMessage(
        JSON.stringify({ text: Locale.systemMsg.chatEnd }),
        {
          senderType: MessageSender.USER,
          msgType: MessageType.Template,
          noNeedSendToWebsocket: true,
        },
      );
      if (pushSatisfy) {
        webSocketService.sendMessage("", {
          senderType: MessageSender.USER,
          msgType: MessageType.Satisfy,
          noNeedSendToWebsocket: true,
        });
      }
      setImServiceChannel(ImServiceChannel.Robot);
      // 主动关闭会话
      setEndChatVisible(false);
    }
  };

  const handleEndChatCancel = () => {
    setEndChatVisible(false);
  };
  // 撤回后点击「重新编辑」：填入内容并滚动到输入框
  useEffect(() => {
    if (reEditContent != null) {
      setInputVal(reEditContent);
      setReEditContent(null);
      textareaRef.current?.focus();
      requestAnimationFrame(() => {
        inputContainerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      });
    }
  }, [reEditContent, setReEditContent]);

  /** 人工会话：走 WS 发用户文本（断线时 sendMessage 内部会轮询等待重连后再发） */
  const sendHumanSessionText = (
    val: string,
    opts?: { skipRealtimeTypingEnd?: boolean },
  ) => {
    if (!val.trim()) return;
    if (chatStatus !== CurrentChatStatus.LineUp) {
      setCurrentChatStatus(CurrentChatStatus.Online);
    }
    void webSocketService.sendMessage(JSON.stringify({ text: `${val}` }));
    setInputVal("");
    props.handleScrollToBottom?.(
      ScrollAction.FORCE_TO_BOTTOM,
      ScrollFrom.SEND_MSG,
    );
    if (!isOverseaEnv() && !opts?.skipRealtimeTypingEnd) {
      setTimeout(() => {
        webSocketService.sendMessage(`{"text":""}`, {
          msgType: MessageNotRender.RealTime,
          headerType: HeaderType.SYSTEM,
          noNeedInsertToMsgList: true,
        });
      }, STOP_TYPING_DELAY);
    }
  };

  const sendMsg = (val: string) => {
    // 断线时：若仍为人工会话链路，优先触发重连，勿误走机器人 SSE
    if (disconnected) {
      if (imServiceChannel === ImServiceChannel.Human) {
        webSocketService.handleReconnectOffline();
        Toast.warning(Locale.chatInput.connecting);
        // 仍需发起发送：sendMessage 在 WS 未就绪时会 autoRetryMessage 等待建连
        // 断线时暂不推「停止正在输入」：STOP_TYPING_DELAY 后 WS 未必已连上，会徒增一条系统消息的 autoRetry
        sendHumanSessionText(val, { skipRealtimeTypingEnd: true });
        return;
      }
      const content = val.trim();
      setInputVal("");
      handleSSESend(content, true);
      props.handleScrollToBottom?.(
        ScrollAction.FORCE_TO_BOTTOM,
        ScrollFrom.SEND_MSG,
      );
      return;
    }
    if (val.trim()) {
      sendHumanSessionText(val);
    }
  };

  const handleCompositionStart = () => {
    isComposing.current = true;
  };

  // const handleCompositionEnd = (
  //   e: React.CompositionEvent<HTMLTextAreaElement>,
  // ) => {
  //   isComposing.current = false;
  //   handleRealChange(e);
  // };

  // 如果是移动端，使用移动端组件
  if (isMobile) {
    return (
      <MobileChatInput
        onSend={(message) => {
          sendMsg(message);
        }}
        disconnected={disconnected}
        onchangeInput={onchangeInput}
        pageVisible={props.pageVisible}
        isSending={isSending}
        stopStreaming={stopStreaming}
        reEditContent={reEditContent}
        setReEditContent={setReEditContent}
      />
    );
  }

  const disableSendMsg = () => {
    if (inputVal.trim().length === 0) {
      return true;
    }
    return disconnected;
  };

  // const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
  //   const value = e.target.value;
  //   if (!isComposing.current) {
  //     // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //     handleRealChange(e as any);
  //   }
  //   setInputVal(value);
  // };

  const handleEmojiSelect = (emoji: string) => {
    const input = inputRef.current;
    if (input) {
      const cursorPosition = input.selectionStart || 0;
      const newValue =
        inputVal.slice(0, cursorPosition) +
        emoji +
        inputVal.slice(cursorPosition);

      setInputVal(newValue);

      // 不经过 AutoComplete 的 onChange，需自行同步 typing（与 handleAutoChange 一致）
      onchangeInput(newValue);

      // 设置光标位置到表情后面
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(
          cursorPosition + emoji.length,
          cursorPosition + emoji.length,
        );
      }, 0);
    }
  };

  const toggleEmojiPanel = () => {
    if (disconnected) {
      return;
    }
    setShowEmojiPanel(!showEmojiPanel);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // 阻止默认的换行行为
      sendMsg(inputVal);
    }
  };

  const handleSelect = (id: string, { label }: { label: string }) => {
    console.log("handleSelect id,label:", id, label);
    const content = label.replace(regex, "");
    const parentContent = inputVal.replace(regex, "");
    sendMsg(content);

    const params: RobotChatPropsType = {
      id,
      language: locale,
      idType: 1,
      data: {
        userId: createUuid(),
      },
      behaviorCollect: requireReportProps({
        bizType: ReportBizTypeEnum.FAQ,
        content,
        contentId: id,
        contentType: ReportKeywordContentEnum.KEYWORD_ASSOCIATION,
        parentContent,
        parentContentId: "",
        parentContentType: ReportParentContentEnum.ASSOCIATION_SEARCH,
      }),
    };
    addToReportQueue({
      bizType: ReportBizTypeEnum.FAQ,
      content,
      contentId: id,
      createTime: new Date().getTime(),
      eventType: ReportEventEnum.CLICK,
      scene: ReportSceneTypeEnum.NORMAL_CONSULT,
      parentContent: parentContent,
      parentContentType: ReportParentContentEnum.ASSOCIATION_SEARCH,
    });
    setInputVal("");

    // 滚动到底部
    props.handleScrollToBottom?.(
      ScrollAction.FORCE_TO_BOTTOM,
      ScrollFrom.SEND_MSG,
    );

    handleRobotChat(params, {
      customerMessage: content,
      parentContentInfo: {
        parentContentId: "",
        parentContent: content,
        parentContentType: ReportParentContentEnum.ASSOCIATION_SEARCH,
      },
    }).then((res) => {
      // TODO
      // const chatContent = res as ChatContentType[];
      // dispatch(batchAddQuestion(chatContent));
    });
  };

  const handleAutoChange = (value: string) => {
    setInputVal(value);
    onchangeInput(value);
  };

  const handleAutoSelect = (value: string, option: any) => {
    // 从 option 中获取原始的 topic 字符串
    const label = option?.topic || option?.label || value;
    handleSelect(value, { label });
  };

  const handleAutoKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMsg(inputVal);
    }
  };

  return (
    <div className={styles.inputContainer} ref={inputContainerRef}>
      <PasteModal visible={pasteFileVisible} handler={modalHandler} />
      {!disconnected && (
        <div className={styles.operationGroup}>
          {showEndChatButton && (
            <Button
              theme="borderless"
              onClick={() => setEndChatVisible(true)}
              type="tertiary"
              className={styles.endButton}
            >
              <SvgEndChat />
              {Locale.button.endMessage}
            </Button>
          )}
          <button
            className={`${styles.operationBtn} ${disconnected ? styles.disabled : ""}`}
            onClick={toggleEmojiPanel}
          >
            <Image
              src={getPublicImagePath("emojis.png")}
              alt="Emoji List"
              width={20}
              height={20}
              unoptimized
            />
          </button>
          {/* TODO: 这里暂时只判断登录态，后续需补充 */}
          <UploadFile disabled={disconnected} />
        </div>
      )}
      {/* 结束会话确认弹窗 */}
      <Modal
        title={Locale.chatBar.tips}
        visible={endChatVisible}
        onCancel={handleEndChatCancel}
        closeOnEsc={true}
        size="small"
        width={300}
        centered
        footer={
          <SemiButton onClick={handleEndChatOk}>
            {Locale.button.confirm}
          </SemiButton>
        }
      >
        {Locale.chatBar.confirmEndMessage}
      </Modal>
      <ChatInputQuoted />
      <div className={styles.inputWrapper}>
        <div className={styles.inputGroup}>
          <AutoComplete
            value={inputVal}
            onChange={handleAutoChange}
            onSelect={handleAutoSelect}
            onKeyDown={handleAutoKeyDown}
            onPaste={(e) => {
              handlePaste(e as React.ClipboardEvent<HTMLTextAreaElement>);
            }}
            imServiceChannel={imServiceChannel}
            placeholder={t.chatInput.inputPlaceholder}
            maxLength={500}
            inputType="input"
            inputRef={inputRef}
            disabled={isSending || !aiTypingEnd}
          />
        </div>
        <div className={styles.divider} />
        {/* sse结束并且打字效果结束才显示发送按钮 */}
        {isSending || !aiTypingEnd ? (
          <Image
            src={getPublicImagePath("stop.svg")}
            alt="stop"
            width={24}
            height={24}
            unoptimized
            className={styles.stopButton}
            onClick={stopStreaming}
          />
        ) : (
          <Image
            src={getPublicImagePath("send_new.svg")}
            alt="send"
            width={24}
            height={24}
            unoptimized
            className={styles.sendButton}
            onClick={() => sendMsg(inputVal)}
          />
        )}
      </div>
      {/* 表情面板 */}
      <EmojiPanel
        isVisible={showEmojiPanel}
        onEmojiSelect={handleEmojiSelect}
        onClose={() => setShowEmojiPanel(false)}
      />
    </div>
  );
}
